import { motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Handshake, DollarSign, Share2, UserCheck, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { Button } from "@/components/ui/button";
import { trackCTAClick } from "@/lib/analytics";
import { Eyebrow } from "@/components/landing/_primitives";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45 },
  },
};

export function AffiliateSection() {
  const { language } = useLanguage();
  const rm = useReducedMotion();
  const navigate = useNavigate();

  const blogPath = pickByLanguage(language, {
    pt: "/blog/programa-de-afiliados-qlivar-como-funciona",
    en: "/blog/qlivar-affiliate-program-how-it-works",
  });

  const steps = [
    {
      icon: Share2,
      step: "01",
      title: pickByLanguage(language, { pt: "Compartilhe seu link único", en: "Share your unique link" }),
      description: pickByLanguage(language, {
        pt: "Divulgue o Qlivar para sua audiência — stories, posts, vídeos ou onde quiser.",
        en: "Promote Qlivar to your audience — stories, posts, videos, wherever you want.",
      }),
    },
    {
      icon: UserCheck,
      step: "02",
      title: pickByLanguage(language, { pt: "Alguém cria uma conta", en: "Someone creates an account" }),
      description: pickByLanguage(language, {
        pt: "Quando um visitante assina qualquer plano pago pelo seu link, a comissão é sua.",
        en: "When someone subscribes to any paid plan through your link, the commission is yours.",
      }),
    },
    {
      icon: DollarSign,
      step: "03",
      title: pickByLanguage(language, { pt: "Receba sua comissão", en: "Receive your commission" }),
      description: pickByLanguage(language, {
        pt: "Pagamento mensal direto, com painel de rastreamento em tempo real.",
        en: "Monthly direct payment, with a real-time tracking dashboard.",
      }),
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      label: pickByLanguage(language, { pt: "Comissão recorrente", en: "Recurring commission" }),
      desc: pickByLanguage(language, { pt: "Ganhe enquanto o cliente ficar ativo", en: "Earn as long as the customer stays" }),
    },
    {
      icon: Handshake,
      label: pickByLanguage(language, { pt: "Sem custo para entrar", en: "Free to join" }),
      desc: pickByLanguage(language, { pt: "Cadastro gratuito, sem taxa de adesão", en: "Free sign-up, no joining fee" }),
    },
    {
      icon: Share2,
      label: pickByLanguage(language, { pt: "Link rastreável", en: "Trackable link" }),
      desc: pickByLanguage(language, { pt: "Cliques, conversões e ganhos em tempo real", en: "Clicks, conversions and earnings in real time" }),
    },
  ];

  const handleJoinClick = () => {
    const label = pickByLanguage(language, { pt: "Quero ser afiliado", en: "Become an affiliate" });
    trackCTAClick(label, "affiliate_section", "/auth");
    navigate("/auth");
  };

  return (
    <section
      id="affiliates"
      className="scroll-mt-24 md:scroll-mt-28 bg-[linear-gradient(180deg,#fffaf5_0%,#fff4e8_100%)] py-20 sm:py-24"
    >
      <div className="container">
        {/* Header */}
        <motion.div
          initial={rm ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: rm ? 0 : 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <Eyebrow withDot accent="15 90% 58%" className="justify-center text-[#9a7258]">
            {pickByLanguage(language, { pt: "Programa de Afiliados", en: "Affiliate Program" })}
          </Eyebrow>

          <h2 className="mt-4 font-editorial font-semibold text-[clamp(2.5rem,5.5vw,4rem)] leading-[1.02] tracking-[-0.035em] text-[#201814]">
            {pickByLanguage(language, {
              pt: (
                <>
                  Indique o Qlivar e{" "}
                  <span className="text-gradient-creator">ganhe dinheiro</span> de verdade
                </>
              ),
              en: (
                <>
                  Refer Qlivar and{" "}
                  <span className="text-gradient-creator">earn real money</span>
                </>
              ),
            })}
          </h2>

          <p className="mt-5 text-base leading-8 text-[#66584d] sm:text-lg">
            {pickByLanguage(language, {
              pt: "Venda contas do Qlivar para a sua audiência e receba comissão recorrente toda vez que alguém assinar. Quanto mais indicar, mais você ganha.",
              en: "Sell Qlivar accounts to your audience and earn recurring commission every time someone subscribes. The more you refer, the more you earn.",
            })}
          </p>
        </motion.div>

        {/* How it works — 3 steps with peach watermark numbers */}
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.step}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative overflow-hidden rounded-[28px] border border-[#eadbcc] bg-white/95 p-7 shadow-[0_22px_55px_rgba(110,79,52,0.06)]"
              >
                {/* Watermark number — peach editorial serif */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-2 select-none font-editorial text-[5rem] font-semibold leading-none text-[#fcd4bd]"
                >
                  {s.step}
                </span>

                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ffe6d3] text-[#ef7b43]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-editorial text-[1.55rem] leading-[1.1] tracking-[-0.02em] text-[#241b17]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-[#66584d]">{s.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Benefits strip + CTA */}
        <motion.div
          initial={rm ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: rm ? 0 : 0.45 }}
          className="mt-8 rounded-[28px] border border-[#eadbcc] bg-white p-6 shadow-[0_22px_55px_rgba(110,79,52,0.06)] md:p-8"
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 flex-col gap-5 sm:flex-row sm:gap-6">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffe6d3] text-[#ef7b43]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold leading-tight text-[#241b17]">{b.label}</p>
                      <p className="mt-1 text-xs leading-5 text-[#78695d]">{b.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="hero"
              size="xl"
              className="shrink-0 transition-colors focus-visible:ring-primary/40"
              onClick={handleJoinClick}
            >
              {pickByLanguage(language, { pt: "Quero ser afiliado", en: "Become an affiliate" })}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>

        {/* Fine print */}
        <p className="mt-6 text-center text-xs leading-6 text-[#78695d]">
          {pickByLanguage(language, { pt: "Ao participar você concorda com os ", en: "By joining you agree to the " })}
          <Link to="/affiliate/terms" className="font-medium text-[#4d4038] transition-colors hover:text-[#201814] hover:underline">
            {pickByLanguage(language, { pt: "Termos do Programa", en: "Program Terms" })}
          </Link>
          {" · "}
          <Link to="/affiliate/payments-policy" className="font-medium text-[#4d4038] transition-colors hover:text-[#201814] hover:underline">
            {pickByLanguage(language, { pt: "Política de Pagamentos", en: "Payment Policy" })}
          </Link>
          {" · "}
          <Link to="/affiliate/compliance-policy" className="font-medium text-[#4d4038] transition-colors hover:text-[#201814] hover:underline">
            {pickByLanguage(language, { pt: "Compliance", en: "Compliance" })}
          </Link>
          {" · "}
          <Link
            to={blogPath}
            className="inline-flex items-center gap-0.5 font-medium text-[#ef7b43] transition-colors hover:text-[#c65c28] hover:underline"
          >
            {pickByLanguage(language, { pt: "Guia completo", en: "Full guide" })}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </p>
      </div>
    </section>
  );
}
